import React, { useEffect, useState } from "react";
import {
  startOfWeek,
  endOfWeek,
  format,
  addDays,
  startOfDay,
  addWeeks,
  subWeeks,
  isAfter,
  isBefore,
  parseISO,
  startOfToday,
} from "date-fns";
import { useTheme } from "@mui/material/styles";

import { Box, IconButton, Stack, Typography } from "@mui/material";

import useCall from "../../hooks/useCall";
import useBrowser from "../../hooks/useBrowser";
import useProject from "../../hooks/useProject";
import useLocalization from "../../hooks/useLocalization";

import Loading from "./Loading";
import Call from "./Call";
import MUIIcon from "./MUIIcon";

import Select from "../elements/Select";

const WeeklyCalls = ({ projectId, syndicationId, onRefresh, refreshData }) => {
  const isSyndicationMode = !!syndicationId;
  const isProjectMode = !!projectId;

  // Only initialize project context if in project mode
  const projectContext = useProject(isProjectMode ? projectId : null);
  const { project } = projectContext;

  const { calls, getCalls, filterCallsForDate, clearCalls } = useCall();
  const { windowHeight } = useBrowser();
  const { provinceList } = useLocalization();
  const theme = useTheme();

  const [weekStart, setWeekStart] = useState(null);
  const [weekEnd, setWeekEnd] = useState(null);
  const [filter, setFilter] = useState(null);
  const [projects, setProjects] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [reps, setReps] = useState([]);
  const [banners, setBanners] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [storeNumbers, setStoreNumbers] = useState([]);

  // Initialize week based on mode
  useEffect(() => {
    if (weekStart) return;

    if (isProjectMode && project) {
      if (isBefore(startOfToday(), project.startsOn)) {
        setWeekStart(startOfWeek(project.startsOn));
        setWeekEnd(endOfWeek(project.startsOn));
      } else {
        setWeekStart(startOfWeek(startOfToday()));
        setWeekEnd(endOfWeek(startOfToday()));
      }
    } else if (isSyndicationMode) {
      // Syndication mode - start from today
      setWeekStart(startOfWeek(startOfToday()));
      setWeekEnd(endOfWeek(startOfToday()));
    }
  }, [project, isProjectMode, isSyndicationMode]);

  // Fetch calls based on week and filters
  useEffect(() => {
    if (!weekStart || !weekEnd) return;

    clearCalls();

    let callFilter = {
      yyyymmdd: {
        $gte: format(weekStart, "yyyy-MM-dd"),
        $lte: format(weekEnd, "yyyy-MM-dd"),
      },
    };

    if (isProjectMode) {
      callFilter.projectId = projectId;
    } else if (isSyndicationMode) {
      callFilter.syndicationId = syndicationId;
    }

    if (filter && Object.keys(filter).length > 0) {
      callFilter = { ...callFilter, ...filter };
    }

    getCalls(callFilter);
  }, [
    weekStart,
    weekEnd,
    filter,
    refreshData,
    isProjectMode,
    isSyndicationMode,
    projectId,
    syndicationId,
  ]);

  // Derive all filters from calls data
  useEffect(() => {
    if (!Array.isArray(calls) || calls.length === 0) return;

    // Projects - only in syndication mode
    if (isSyndicationMode) {
      const uniqueProjectIds = Array.from(
        new Set(calls.map((call) => call.project?.id).filter(Boolean)),
      );
      const projectList = uniqueProjectIds
        .map((pid) => {
          const projectData = calls.find((c) => c.project?.id === pid)?.project;
          return projectData
            ? { id: projectData.id, name: projectData.name }
            : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
      setProjects(projectList);
    }

    // Territories
    const uniqueTerritoryIds = Array.from(
      new Set(calls.map((call) => call.territory?.id).filter(Boolean)),
    );
    const territoryList = uniqueTerritoryIds
      .map((tid) => {
        const territoryData = calls.find(
          (c) => c.territory?.id === tid,
        )?.territory;
        return territoryData
          ? { id: territoryData.id, name: territoryData.name }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
    setTerritories(territoryList);

    // Reps (users)
    const uniqueUserIds = Array.from(
      new Set(calls.map((call) => call.userId).filter(Boolean)),
    );
    const repList = uniqueUserIds
      .map((uid) => {
        const callData = calls.find((c) => c.userId === uid);
        return callData
          ? { id: uid, name: callData.repName || "Unassigned" }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
    setReps(repList);

    // Banners
    const uniqueBannerIds = Array.from(
      new Set(calls.map((call) => call.banner?.id).filter(Boolean)),
    );
    const bannerList = uniqueBannerIds
      .map((bid) => {
        const bannerData = calls.find((c) => c.banner?.id === bid)?.banner;
        return bannerData ? { id: bannerData.id, name: bannerData.name } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
    setBanners(bannerList);

    // Provinces
    const uniqueProvinces = Array.from(
      new Set(calls.map((call) => call.province).filter(Boolean)),
    );
    const provinceFullList = provinceList();
    const mappedProvinces = uniqueProvinces
      .map((provinceCode) => {
        const fullName = provinceFullList.find((p) => p.id === provinceCode);
        return fullName || { id: provinceCode, name: provinceCode };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    setProvinces(mappedProvinces);

    // Store numbers
    const uniqueStoreNumbers = Array.from(
      new Set(
        calls
          .map((call) => call.locationNumber ?? call.location?.locationNumber)
          .filter((value) => value !== null && value !== undefined)
          .map((value) => `${value}`.trim())
          .filter(Boolean),
      ),
    );
    const storeNumberList = uniqueStoreNumbers
      .map((value) => ({ id: value, name: value }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    setStoreNumbers(storeNumberList);
  }, [calls, isSyndicationMode]);

  const weeklyHeader = () => {
    let days = [];

    for (let i = 0; i < 7; i++) {
      days.push(
        <Box
          key={`header-${i}`}
          sx={{
            flex: 1,
            backgroundColor: theme.palette.primary.light,
            border: `solid 1px ${theme.palette.primary.main}`,
            padding: "4px",
          }}
        >
          <Typography
            sx={{ fontSize: "10px", fontWeight: "bold" }}
            textAlign="center"
          >
            {format(addDays(weekStart, i), "EEEE")}
          </Typography>
          <Typography
            sx={{ fontSize: "12px", fontWeight: "bold" }}
            textAlign="center"
          >
            {format(addDays(weekStart, i), "MMM dd")}
          </Typography>
        </Box>,
      );
    }
    return days;
  };

  const weekDay = () => {
    let days = [];

    for (let i = 0; i < 7; i++) {
      const dailyCalls = filterCallsForDate(startOfDay(addDays(weekStart, i)));

      // const dockets = getDockets(dailyCalls);

      days.push(
        <Box
          key={`day-${i}`}
          sx={{
            flex: 1,
            border: `solid 1px ${theme.palette.primary.main}`,
            padding: "4px",
            height: `${windowHeight - 320}px`,
            overflow: "auto",
          }}
        >
          <Stack spacing={1}>
            {dailyCalls.map((call) => (
              <Call
                key={call.id}
                call={call}
                showProject={isSyndicationMode}
                onRefresh={onRefresh}
              />
            ))}
          </Stack>
        </Box>,
      );
    }

    return days;
  };

  const handlePrevWeek = () => {
    setWeekStart(subWeeks(weekStart, 1));
    setWeekEnd(subWeeks(weekEnd, 1));
  };

  const handleNextWeek = () => {
    setWeekStart(addWeeks(weekStart, 1));
    setWeekEnd(addWeeks(weekEnd, 1));
  };

  const handleFilter = (name, value) => {
    const normalizedValue =
      typeof value === "string"
        ? value.trim()
        : value === undefined || value === null
          ? ""
          : value;
    const shouldRemove =
      normalizedValue === "all" || normalizedValue === "" || !normalizedValue;

    if (!filter && !shouldRemove) {
      setFilter({ [name]: normalizedValue });
    } else {
      setFilter((current) => {
        let newFilter = { ...current };
        if (!shouldRemove) {
          newFilter = { ...newFilter, [name]: normalizedValue };
        } else {
          delete newFilter[name];
        }

        if (Object.keys(newFilter).length == 0) {
          newFilter = null;
        }

        return newFilter;
      });
    }
  };

  if (!calls) return <Loading />;

  // Navigation - only disable for project mode
  const canGoPrev = isProjectMode
    ? project && !isAfter(project.startsOn, weekStart)
    : true;
  const canGoNext = isProjectMode
    ? project && project.endsOn && !isBefore(project.endsOn, weekEnd)
    : true;

  return (
    <Box>
      <Box>
        <Box
          sx={{
            height: "40",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px",
          }}
        >
          <Box>
            <IconButton
              onClick={handlePrevWeek}
              disabled={!canGoPrev}
            >
              <MUIIcon name="ArrowCircleLeft" />
            </IconButton>
          </Box>

          <Box
            sx={{ width: "calc(100% - 40px)", display: "flex" }}
            gap={1}
          >
            {isSyndicationMode &&
              Array.isArray(projects) &&
              projects.length > 0 && (
                <Select
                  label="Projects"
                  items={projects}
                  onChange={(newValue) => handleFilter("projectId", newValue)}
                  value={filter && filter.projectId ? filter.projectId : "all"}
                  includeNone={true}
                  noneLabel="All Projects"
                />
              )}
            {Array.isArray(territories) && territories.length > 0 && (
              <Select
                label="Territories"
                items={territories}
                onChange={(newValue) => handleFilter("territoryId", newValue)}
                value={
                  filter && filter.territoryId ? filter.territoryId : "all"
                }
                includeNone={true}
                noneLabel="All Territories"
              />
            )}
            {Array.isArray(reps) && reps.length > 0 && (
              <Select
                label="Reps"
                items={reps}
                onChange={(newValue) => handleFilter("userId", newValue)}
                value={filter && filter.userId ? filter.userId : "all"}
                includeNone={true}
                noneLabel="All Reps"
              />
            )}
            {Array.isArray(banners) && banners.length > 0 && (
              <Select
                label="Banners"
                items={banners}
                onChange={(newValue) => handleFilter("bannerId", newValue)}
                value={filter && filter.bannerId ? filter.bannerId : "all"}
                includeNone={true}
                noneLabel="All Banners"
              />
            )}
            {Array.isArray(provinces) && provinces.length > 0 && (
              <Select
                label="Provinces"
                items={provinces}
                onChange={(newValue) => handleFilter("province", newValue)}
                value={filter && filter.province ? filter.province : "all"}
                includeNone={true}
                noneLabel="All Provinces"
              />
            )}
            {Array.isArray(storeNumbers) && storeNumbers.length > 0 && (
              <Select
                label="Store Number"
                items={storeNumbers}
                onChange={(newValue) =>
                  handleFilter("locationNumber", newValue)
                }
                value={
                  filter && filter.locationNumber ? filter.locationNumber : "all"
                }
                includeNone={true}
                noneLabel="All Store Numbers"
              />
            )}
            <Select
              label="Call Status"
              items={[
                { id: "future", name: "Future" },
                { id: "incomplete", name: "Incomplete" },
                { id: "complete", name: "Complete" },
              ]}
              onChange={(newValue) => handleFilter("callStatus", newValue)}
              value={filter && filter.callStatus ? filter.callStatus : "all"}
              includeNone={true}
              noneLabel="All"
            />
          </Box>

          <Box>
            <IconButton
              onClick={handleNextWeek}
              disabled={!canGoNext}
            >
              <MUIIcon name="ArrowCircleRight" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ display: "flex" }}>{weeklyHeader()}</Box>
      </Box>
      <Box sx={{ display: "flex" }}>{weekDay()}</Box>
    </Box>
  );
};

export default WeeklyCalls;
